import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import {
  Plus, Trash2, X, CheckCircle2, Circle, Briefcase, GripVertical, ArrowUpDown,
  DollarSign, Star, Sparkles, RefreshCw, Repeat, TrendingUp, Wand2,
} from "lucide-react";
import { TicketCard } from "../components/TicketCard";
import { useAuth } from "../lib/auth";
import {
  DndContext, PointerSensor, KeyboardSensor, useSensor, useSensors, closestCenter,
} from "@dnd-kit/core";
import {
  SortableContext, arrayMove, rectSortingStrategy,
  sortableKeyboardCoordinates, useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { SourceSelect } from "../components/SourceSelect";

// The 4 placement tabs — border colors + labels flow from here.
const PLACEMENTS = [
  { key: "active",          label: "Active Projects",  color: "#0066FF" },
  { key: "sales_pipeline",  label: "Sales Pipeline",   color: "#EF4444" },
  { key: "passive",         label: "Passive Projects", color: "#10B981" },
  { key: "completed",       label: "Completed",        color: "#9CA3AF" },
];

// Importance level → color ramp (unchanged): ! orange → !!!!! maroon.
const IMPORTANCE_COLORS = { 1: "#F59E0B", 2: "#EA580C", 3: "#DC2626", 4: "#B91C1C", 5: "#7F1D1D" };
const IMPORTANCE_LABELS = ["", "!", "!!", "!!!", "!!!!", "!!!!!"];

const SORTS = [
  { key: "manual",     label: "Manual order" },
  { key: "recent",     label: "Recent" },
  { key: "importance", label: "Importance" },
];

const STAGE_LABEL = {
  sales_pipeline: "Sales Pipeline",
  verbal_commitment: "Verbal Commit",
  in_progress: "In Progress",
  awaiting_payment: "Awaiting Payment",
};

export default function Projects() {
  const { user } = useAuth();
  const readOnly = user?.role === "viewer";
  const [projects, setProjects] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [clients, setClients] = useState([]);
  const [meta, setMeta] = useState(null);
  const [stubs, setStubs] = useState([]);
  const [placement, setPlacement] = useState("active");
  const [sortBy, setSortBy] = useState("manual");
  const [creating, setCreating] = useState(false);
  const [showMigrate, setShowMigrate] = useState(false);
  const [showStubs, setShowStubs] = useState(false);
  const navigate = useNavigate();

  const load = async () => {
    const [p, t, c, m, st] = await Promise.all([
      api.get("/projects"),
      api.get("/tickets?status=active"),
      api.get("/clients"),
      api.get("/project-meta"),
      api.get("/crm/stub-clients").catch(() => ({ data: [] })),
    ]);
    setProjects(p.data);
    setTickets(t.data);
    setClients(c.data);
    setMeta(m.data);
    setStubs(st.data || []);
  };
  useEffect(() => { load(); }, []);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const clientNameById = useMemo(() => {
    const m = {}; clients.forEach((c) => { m[c.user_id] = c.name; }); return m;
  }, [clients]);

  const byPlacement = useMemo(() => {
    const buckets = { active: [], sales_pipeline: [], passive: [], completed: [] };
    for (const p of projects) {
      const key = p.placement || (p.status === "complete" ? "completed" : "active");
      if (buckets[key]) buckets[key].push(p);
    }
    return buckets;
  }, [projects]);

  const visibleProjects = useMemo(() => {
    const list = [...(byPlacement[placement] || [])];
    if (sortBy === "recent") {
      list.sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
    } else if (sortBy === "importance") {
      list.sort((a, b) => (b.importance || 3) - (a.importance || 3));
    } else {
      list.sort((a, b) => {
        const pa = a.position ?? 1e9, pb = b.position ?? 1e9;
        if (pa !== pb) return pa - pb;
        return (b.created_at || "").localeCompare(a.created_at || "");
      });
    }
    return list;
  }, [byPlacement, placement, sortBy]);

  const toggleStatus = async (p) => {
    // Marking complete: move to Completed placement AND set subscription_tier to none.
    // Marking active: revert to Active.
    const next = p.status === "active" ? "complete" : "active";
    const patch = { status: next };
    if (next === "complete") {
      patch.placement = "completed";
      patch.subscription_tier = "none";
      patch.subscription_amount = 0;
    } else if (p.placement === "completed") {
      patch.placement = "active";
    }
    try {
      await api.put(`/projects/${p.id}`, patch);
      toast.success(next === "complete" ? "Marked complete" : "Marked active");
      load();
    } catch { toast.error("Failed to update"); }
  };

  const moveTo = async (p, next) => {
    if (next === p.placement) return;
    const patch = { placement: next };
    if (next === "completed") { patch.subscription_tier = "none"; patch.subscription_amount = 0; }
    try {
      await api.put(`/projects/${p.id}`, patch);
      toast.success(`Moved to ${PLACEMENTS.find((x) => x.key === next)?.label}`);
      load();
    } catch { toast.error("Failed to move project"); }
  };

  const remove = async (p) => {
    if (p.status !== "complete") { toast.error("Only completed projects can be deleted"); return; }
    if (!window.confirm(`Delete "${p.title}"?`)) return;
    try {
      await api.delete(`/projects/${p.id}`);
      toast.success("Deleted");
      load();
    } catch { toast.error("Delete failed"); }
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = visibleProjects.findIndex((p) => p.id === active.id);
    const newIndex = visibleProjects.findIndex((p) => p.id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    const reordered = arrayMove(visibleProjects, oldIndex, newIndex);
    setProjects((prev) => {
      const rest = prev.filter((x) => !reordered.find((r) => r.id === x.id));
      return [...rest, ...reordered.map((p, i) => ({ ...p, position: i }))];
    });
    setSortBy("manual");
    try { await api.put("/projects-reorder", { order: reordered.map((p) => p.id) }); }
    catch { toast.error("Couldn't save order"); load(); }
  };

  const runMigration = async () => {
    try {
      const { data } = await api.post("/crm/migrate");
      toast.success(`${data.created_projects} project(s) created`);
      setShowMigrate(false);
      await load();
      if (stubs.length || data.created_stub_clients) setShowStubs(true);
    } catch (e) { toast.error(e?.response?.data?.detail || "Migration failed"); }
  };

  const activeTickets = tickets;

  return (
    <div data-testid="projects-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-xs font-semibold uppercase tracking-widest text-zinc-500">Pipeline</div>
          <h1 className="mt-1 text-3xl font-semibold tracking-tight text-zinc-900">Projects</h1>
          <p className="mt-2 text-sm text-zinc-600">One record per project. Move between Active, Sales Pipeline, Passive and Completed.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {stubs.length > 0 && (
            <button
              data-testid="projects-stub-queue"
              onClick={() => setShowStubs(true)}
              className="inline-flex items-center gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm font-medium text-amber-800 hover:bg-amber-100"
              title="Review clients auto-created by migration"
            >
              <Sparkles size={14} /> {stubs.length} clients need setup
            </button>
          )}
          {!readOnly && (
            <>
              <button
                data-testid="projects-migrate"
                onClick={() => setShowMigrate(true)}
                className="inline-flex items-center gap-2 rounded-md border border-zinc-200 bg-white px-3 py-2 text-sm font-medium text-zinc-700 hover:bg-zinc-50"
                title="Convert existing leads + subscriptions into projects"
              >
                <RefreshCw size={14} /> Import CRM Data
              </button>
              <button
                data-testid="projects-new-button"
                onClick={() => setCreating(true)}
                className="inline-flex items-center gap-2 rounded-md bg-[#0066ff] px-4 py-2 text-sm font-medium text-white hover:bg-[#0052cc] active:scale-[0.98]"
              >
                <Plus size={16} /> New Project
              </button>
            </>
          )}
        </div>
      </div>

      {/* Placement tabs */}
      <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-1 rounded-lg border border-zinc-200 bg-white p-1">
          {PLACEMENTS.map((p) => (
            <TabButton
              key={p.key}
              id={p.key}
              tab={placement}
              setTab={setPlacement}
              accent={p.color}
              count={(byPlacement[p.key] || []).length}
            >
              {p.label}
            </TabButton>
          ))}
          <TabButton id="work-orders" tab={placement} setTab={setPlacement} accent="#0066FF" count={activeTickets.length}>
            Work Orders
          </TabButton>
        </div>

        {placement !== "work-orders" && (
          <div data-testid="projects-sort" className="flex items-center gap-2 rounded-lg border border-zinc-200 bg-white px-3 py-1.5 text-xs text-zinc-600">
            <ArrowUpDown size={12} className="text-zinc-400" />
            <span className="font-medium">Sort</span>
            <select
              data-testid="projects-sort-select"
              value={sortBy} onChange={(e) => setSortBy(e.target.value)}
              className="bg-transparent text-xs font-semibold text-zinc-900 focus:outline-none"
            >
              {SORTS.map((s) => (<option key={s.key} value={s.key}>{s.label}</option>))}
            </select>
          </div>
        )}
      </div>

      {placement === "work-orders" ? (
        <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {activeTickets.map((t) => (
            <TicketCard key={t.id} ticket={t} clientName={clientNameById[t.client_id] || ""} hideClient={false} onClick={() => navigate(`/tickets/${t.id}`)} />
          ))}
          {activeTickets.length === 0 && (
            <div className="col-span-full rounded-xl border border-dashed border-zinc-200 bg-white p-10 text-center text-sm text-zinc-500">
              No active work tickets across clients.
            </div>
          )}
        </div>
      ) : visibleProjects.length === 0 ? (
        <div className="mt-6 rounded-xl border border-dashed border-zinc-200 bg-white p-12 text-center">
          <Briefcase size={28} className="mx-auto text-zinc-300" />
          <p className="mt-3 text-sm text-zinc-500">No projects in this tab.</p>
          {!readOnly && placement === "active" && (
            <button data-testid="projects-empty-create" onClick={() => setCreating(true)} className="mt-4 inline-flex items-center gap-1.5 rounded-md bg-[#0066ff] px-3 py-2 text-sm font-medium text-white hover:bg-[#0052cc]">
              <Plus size={14} /> New Project
            </button>
          )}
        </div>
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={(readOnly || sortBy !== "manual") ? undefined : handleDragEnd}>
          <SortableContext items={visibleProjects.map((p) => p.id)} strategy={rectSortingStrategy}>
            <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
              {visibleProjects.map((p) => (
                <SortableProjectCard
                  key={p.id}
                  project={p}
                  readOnly={readOnly}
                  draggable={!readOnly && sortBy === "manual"}
                  meta={meta}
                  onOpen={() => navigate(`/projects/${p.id}`)}
                  onToggleStatus={() => toggleStatus(p)}
                  onMove={(next) => moveTo(p, next)}
                  onDelete={() => remove(p)}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}

      {placement !== "work-orders" && sortBy !== "manual" && !readOnly && (
        <div className="mt-3 text-[11px] text-zinc-500">
          Switch to <strong className="text-zinc-700">Manual order</strong> to drag and rearrange.
        </div>
      )}

      {creating && meta && (
        <ProjectFormModal
          meta={meta} clients={clients}
          onClose={() => setCreating(false)}
          onSaved={(p) => { setCreating(false); load(); if (p?.id) navigate(`/projects/${p.id}`); }}
        />
      )}

      {showMigrate && (
        <MigrateModal onClose={() => setShowMigrate(false)} onConfirm={runMigration} />
      )}

      {showStubs && (
        <StubReviewModal
          stubs={stubs}
          clients={clients.filter((c) => !c.needs_setup)}
          onClose={() => setShowStubs(false)}
          onResolved={async () => { await load(); }}
        />
      )}
    </div>
  );
}

function TabButton({ id, tab, setTab, children, accent, count }) {
  const active = tab === id;
  return (
    <button
      data-testid={`projects-tab-${id}`}
      onClick={() => setTab(id)}
      className={`flex items-center rounded-md px-3 py-2 text-sm font-medium transition-colors ${active ? "bg-white text-zinc-900 shadow-sm" : "text-zinc-600 hover:bg-zinc-50"}`}
      style={active ? { boxShadow: `inset 0 -2px 0 ${accent}` } : undefined}
    >
      {children}
      {typeof count === "number" && count > 0 && (
        <span
          className="ml-2 rounded-full px-1.5 py-0.5 text-[10px] font-semibold"
          style={active ? { backgroundColor: `${accent}20`, color: accent } : { backgroundColor: "#F4F4F5", color: "#71717A" }}
        >
          {count}
        </span>
      )}
    </button>
  );
}

function SortableProjectCard(props) {
  const { project, draggable } = props;
  const sortable = useSortable({ id: project.id, disabled: !draggable });
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = sortable;
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
    zIndex: isDragging ? 30 : "auto",
  };
  return (
    <div ref={setNodeRef} style={style} data-testid={`project-sortable-${project.id}`}>
      <ProjectCard {...props} dragAttributes={attributes} dragListeners={listeners} />
    </div>
  );
}

function ProjectCard({ project, readOnly, draggable, meta, onOpen, onToggleStatus, onMove, onDelete, dragAttributes, dragListeners }) {
  const isComplete = project.status === "complete" || project.placement === "completed";
  const tasksDone = (project.tasks || []).filter((t) => t.completed).length;
  const tasksTotal = (project.tasks || []).length;
  const importance = project.importance ?? project.priority ?? 3;
  const importanceColor = IMPORTANCE_COLORS[Math.max(1, Math.min(5, importance))];
  const revenue = parseFloat(project.expected_revenue || project.total_revenue || 0) || 0;
  const catLabel = (meta?.categories || []).find((c) => c.key === project.category)?.label || project.category;
  const notesPreview = (project.notes || "").trim();
  const showConfidence = project.placement === "sales_pipeline";
  const isSub = project.subscription_tier && project.subscription_tier !== "none";

  return (
    <div
      data-testid={`project-card-${project.id}`}
      onClick={onOpen}
      className={`group relative cursor-pointer rounded-xl border border-zinc-200 bg-white p-5 shadow-sm transition-all hover:-translate-y-[1px] hover:shadow-md ${isComplete ? "opacity-60 grayscale" : ""}`}
    >

      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span
              data-testid={`project-card-${project.id}-importance`}
              className="rounded-md px-2 py-0.5 text-[11px] font-bold tracking-wider text-white"
              style={{ backgroundColor: importanceColor }}
              title={`Importance ${importance} / 5`}
            >
              {IMPORTANCE_LABELS[importance] || "!"}
            </span>
            {revenue > 0 && (
              <span data-testid={`project-card-${project.id}-revenue`} className="inline-flex items-center gap-1 rounded-md bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-700">
                <DollarSign size={11} />
                {revenue.toLocaleString(undefined, { maximumFractionDigits: 0 })}
              </span>
            )}
            {showConfidence && (
              <span data-testid={`project-card-${project.id}-confidence`} className="inline-flex items-center gap-1 rounded-md bg-blue-50 px-2 py-0.5 text-[11px] font-semibold text-[#0066ff]">
                <TrendingUp size={11} /> {project.confidence ?? 5}/10
              </span>
            )}
            {project.stage && (project.placement === "sales_pipeline" || project.placement === "active") && (
              <span className="rounded-md bg-zinc-100 px-2 py-0.5 text-[10px] font-medium text-zinc-700">{STAGE_LABEL[project.stage] || project.stage}</span>
            )}
            {isSub && (
              <span data-testid={`project-card-${project.id}-hosting`} className="inline-flex items-center gap-1 rounded-md bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold text-emerald-800">
                <Repeat size={10} /> Hosting · {project.subscription_tier.charAt(0).toUpperCase() + project.subscription_tier.slice(1)} · ${project.subscription_amount}/mo
              </span>
            )}
            {isSub && project.billing_mode === "automatic" && (
              <span className="inline-flex items-center gap-1 rounded-md bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-800" title="Auto-billed">
                AUTO
              </span>
            )}
          </div>
          <div className="mt-2 truncate text-base font-semibold text-zinc-900">{project.title}</div>
          <div className="mt-0.5 truncate text-xs text-zinc-500">
            {catLabel}
            {project.client_name ? ` · ${project.client_name}` : ""}
          </div>
          {project.source && (
            <span data-testid={`project-card-${project.id}-source`} className="mt-2 inline-flex max-w-full items-center truncate rounded-md border border-zinc-200 bg-zinc-50 px-2 py-0.5 text-[10px] font-medium text-zinc-600" title="Lead source">
              Source: {project.source}
            </span>
          )}
        </div>
        {draggable && (
          <button
            data-testid={`project-card-${project.id}-drag`}
            {...(dragAttributes || {})} {...(dragListeners || {})}
            onClick={(e) => e.preventDefault()}
            className="cursor-grab touch-none rounded p-1 text-zinc-300 opacity-0 transition-opacity hover:bg-zinc-50 hover:text-zinc-600 active:cursor-grabbing group-hover:opacity-100"
            title="Drag to reorder"
          >
            <GripVertical size={14} />
          </button>
        )}
      </div>

      {notesPreview && (
        <div className="mt-3 line-clamp-3 whitespace-pre-wrap text-xs text-zinc-600">{notesPreview}</div>
      )}

      {tasksTotal > 0 && (
        <div className="mt-3 rounded-md border border-zinc-100 bg-zinc-50/50 p-2">
          <div className="flex items-center justify-between text-[10px] font-semibold uppercase tracking-widest text-zinc-500">
            <span>Tasks</span><span>{tasksDone}/{tasksTotal}</span>
          </div>
          <div className="mt-2 flex flex-col gap-1">
            {(project.tasks || []).slice(0, 4).map((t) => (
              <div key={t.id} className="flex items-start gap-1.5 text-[11px]">
                {t.completed ? <CheckCircle2 size={11} className="mt-0.5 flex-shrink-0 text-emerald-500" /> : <Circle size={11} className="mt-0.5 flex-shrink-0 text-zinc-300" />}
                <span className={`truncate ${t.completed ? "text-zinc-400 line-through" : "text-zinc-700"}`}>{t.title}</span>
              </div>
            ))}
            {(project.tasks || []).length > 4 && (
              <div className="text-[10px] italic text-zinc-400">+ {project.tasks.length - 4} more…</div>
            )}
          </div>
        </div>
      )}

      <div className="mt-4 flex items-center justify-between gap-2 border-t border-zinc-100 pt-3">
        {!readOnly ? (
          <button
            data-testid={`project-card-${project.id}-toggle`}
            onClick={(e) => { e.stopPropagation(); onToggleStatus(); }}
            className={`inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-[11px] font-medium transition-colors ${isComplete ? "bg-emerald-50 text-emerald-700 hover:bg-emerald-100" : "bg-zinc-100 text-zinc-700 hover:bg-zinc-200"}`}
          >
            {isComplete ? <><CheckCircle2 size={12} /> Complete</> : <><Circle size={12} /> Active</>}
          </button>
        ) : (
          <span className={`inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-[11px] font-medium ${isComplete ? "bg-emerald-50 text-emerald-700" : "bg-zinc-100 text-zinc-700"}`}>
            {isComplete ? <><CheckCircle2 size={12} /> Complete</> : <><Circle size={12} /> Active</>}
          </span>
        )}
        <div className="flex items-center gap-1.5">
        {!readOnly && (
          <select
            data-testid={`project-card-${project.id}-move`}
            value={project.placement || (isComplete ? "completed" : "active")}
            onClick={(e) => e.stopPropagation()}
            onChange={(e) => { e.stopPropagation(); onMove(e.target.value); }}
            className="rounded-md border border-zinc-200 bg-white px-2 py-1 text-[11px] font-medium text-zinc-700 hover:bg-zinc-50 focus:border-[#0066ff] focus:outline-none"
            title="Move to another section"
          >
            {PLACEMENTS.map((pl) => (<option key={pl.key} value={pl.key}>{pl.key === (project.placement || "active") ? `In: ${pl.label}` : `Move to ${pl.label}`}</option>))}
          </select>
        )}
        {!readOnly && isComplete && (
          <button
            data-testid={`project-card-${project.id}-delete`}
            onClick={(e) => { e.stopPropagation(); onDelete(); }}
            className="rounded-md p-1.5 text-zinc-400 hover:bg-red-50 hover:text-red-600"
            title="Delete"
          >
            <Trash2 size={14} />
          </button>
        )}
        </div>
      </div>
    </div>
  );
}

// ============================ New / Edit Project Modal ============================
export function ProjectFormModal({ meta, clients, initial, onClose, onSaved }) {
  const isEdit = !!initial?.id;
  const [form, setForm] = useState(() => ({
    title: "",
    client_name: "",
    category: (meta?.categories?.[0]?.key) || "web_design",
    placement: "active",
    stage: "sales_pipeline",
    importance: 3,
    expected_revenue: "",
    total_revenue: "",
    confidence: 5,
    source: "",
    is_special: false,
    subscription_tier: "none",
    billing_frequency: "monthly",
    billing_mode: "manual",
    next_billing_date: "",
    deadline: "",
    notes: "",
    ...(initial || {}),
  }));
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const tier = (meta?.subscription_tiers || []).find((t) => t.key === form.subscription_tier);
  const subAmount = tier?.amount || 0;

  const save = async () => {
    if (!form.title.trim()) { toast.error("Title required"); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        expected_revenue: parseFloat(form.expected_revenue || 0) || 0,
        total_revenue: form.total_revenue !== "" ? parseFloat(form.total_revenue) : null,
        confidence: Math.max(0, Math.min(10, parseInt(form.confidence) || 0)),
        subscription_amount: subAmount,
      };
      // Auto-force placement/stage rules
      if (payload.placement === "active" && payload.stage === "sales_pipeline") payload.stage = "in_progress";
      if (payload.placement === "passive") { payload.subscription_tier = payload.subscription_tier || "essential"; payload.subscription_amount = (meta?.subscription_tiers?.find((t) => t.key === payload.subscription_tier)?.amount) || 0; }
      if (payload.placement === "completed") { payload.status = "complete"; payload.subscription_tier = "none"; payload.subscription_amount = 0; }
      const url = isEdit ? `/projects/${initial.id}` : "/projects";
      const method = isEdit ? "put" : "post";
      const { data } = await api[method](url, payload);
      toast.success(isEdit ? "Saved" : "Project created");
      onSaved(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to save"); }
    finally { setSaving(false); }
  };

  const catName = (meta?.categories || []).find((c) => c.key === form.category)?.label || "";
  const autoTitle = [form.client_name.trim(), catName].filter(Boolean).join(" - ");

  const showSubscription = true;
  const showStage = form.placement === "sales_pipeline" || form.placement === "active";
  const showConfidence = form.placement === "sales_pipeline";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4" onClick={onClose}>
      <div data-testid="project-modal" className="max-h-[92vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-white p-8 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between">
          <div>
            <div className="text-xs font-semibold uppercase tracking-widest text-zinc-500">{isEdit ? "Edit" : "New"}</div>
            <h2 className="mt-1 text-2xl font-semibold tracking-tight text-zinc-900">{isEdit ? "Edit Project" : "Create Project"}</h2>
          </div>
          <button data-testid="project-modal-close" onClick={onClose} className="rounded p-1 text-zinc-400 hover:bg-zinc-100"><X size={18} /></button>
        </div>

        <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2">
          <Field label="Title" required>
            <div className="flex items-center gap-1.5">
              <input data-testid="project-form-title" autoFocus value={form.title} onChange={(e) => set("title", e.target.value)} className={inputCls} />
              <button
                type="button"
                data-testid="project-form-autotitle"
                onClick={(e) => { e.preventDefault(); set("title", autoTitle); }}
                disabled={!form.client_name.trim()}
                className="inline-flex flex-shrink-0 items-center gap-1 rounded-md border border-zinc-200 bg-white px-2.5 py-2 text-xs font-medium text-zinc-700 hover:border-[#0066ff] hover:text-[#0066ff] disabled:cursor-not-allowed disabled:opacity-40"
                title={form.client_name.trim() ? `Use "${autoTitle}"` : "Add a client name first"}
              >
                <Wand2 size={13} /> Auto
              </button>
            </div>
          </Field>
          <Field label="Type of work">
            <select data-testid="project-form-category" value={form.category} onChange={(e) => set("category", e.target.value)} className={inputCls}>
              {(meta?.categories || []).map((c) => (<option key={c.key} value={c.key}>{c.label}</option>))}
            </select>
          </Field>

          <Field label="Client name">
            <input
              data-testid="project-form-client"
              value={form.client_name}
              onChange={(e) => set("client_name", e.target.value)}
              className={inputCls}
              list="project-form-client-list"
              placeholder="Type a name or pick from your clients"
            />
            <datalist id="project-form-client-list">
              {clients.map((c) => (<option key={c.user_id} value={c.name} />))}
            </datalist>
          </Field>

          <Field label="Placement">
            <select data-testid="project-form-placement" value={form.placement} onChange={(e) => set("placement", e.target.value)} className={inputCls}>
              {PLACEMENTS.map((p) => (<option key={p.key} value={p.key}>{p.label}</option>))}
            </select>
          </Field>

          {showStage && (
            <Field label="Stage">
              <select data-testid="project-form-stage" value={form.stage || ""} onChange={(e) => set("stage", e.target.value)} className={inputCls}>
                <option value="sales_pipeline">Sales Pipeline</option>
                <option value="verbal_commitment">Verbal Commit</option>
                <option value="in_progress">In Progress</option>
                <option value="awaiting_payment">Awaiting Payment</option>
              </select>
            </Field>
          )}

          {showConfidence && (
            <Field label="Confidence (0-10)">
              <input data-testid="project-form-confidence" type="number" min="0" max="10" value={form.confidence} onChange={(e) => set("confidence", e.target.value)} className={inputCls} />
            </Field>
          )}

          <Field label="Lead source">
            <SourceSelect testid="project-form-source" value={form.source} sources={meta?.sources || []} onChange={(v) => set("source", v)} className={inputCls} />
          </Field>

          <Field label="Importance">
            <div data-testid="project-form-importance" className="flex items-center gap-1">
              {[1,2,3,4,5].map((n) => (
                <button key={n} type="button"
                  data-testid={`project-form-importance-${n}`}
                  onClick={() => set("importance", n)}
                  className={`rounded-md px-2.5 py-1.5 text-xs font-bold tracking-wider transition-all ${form.importance === n ? "text-white scale-[1.04]" : "border border-zinc-200 bg-white text-zinc-700 hover:bg-zinc-50"}`}
                  style={form.importance === n ? { backgroundColor: IMPORTANCE_COLORS[n] } : undefined}
                >
                  {IMPORTANCE_LABELS[n]}
                </button>
              ))}
            </div>
          </Field>

          <Field label="Expected revenue ($)">
            <input data-testid="project-form-revenue" type="number" step="0.01" min="0" value={form.expected_revenue} onChange={(e) => set("expected_revenue", e.target.value)} className={inputCls} />
          </Field>

          <Field label="Deadline">
            <input data-testid="project-form-deadline" type="date" value={form.deadline || ""} onChange={(e) => set("deadline", e.target.value)} className={inputCls} />
          </Field>

          <div className="md:col-span-2">
            <label className="flex cursor-pointer items-center gap-3 rounded-lg border border-zinc-200 bg-white p-3 hover:bg-zinc-50">
              <input
                type="checkbox"
                data-testid="project-form-special"
                checked={!!form.is_special}
                onChange={(e) => set("is_special", e.target.checked)}
                className="h-4 w-4 rounded border-zinc-300 text-[#F59E0B] focus:ring-[#F59E0B]"
              />
              <span className="flex items-center gap-1.5 text-sm text-zinc-700">
                <Star size={14} className="text-[#F59E0B]" /> <strong>Special project</strong> — tracked separately from standard on financials
              </span>
            </label>
          </div>

          {showSubscription && (
            <>
              <Field label="Hosting & Maintenance plan">
                <select data-testid="project-form-tier" value={form.subscription_tier} onChange={(e) => set("subscription_tier", e.target.value)} className={inputCls}>
                  {(meta?.subscription_tiers || []).map((t) => (
                    <option key={t.key} value={t.key}>
                      {t.label} {t.amount ? `· $${t.amount}/mo` : ""}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Payment frequency">
                <select data-testid="project-form-frequency" value={form.billing_frequency} onChange={(e) => set("billing_frequency", e.target.value)} className={inputCls}>
                  {(meta?.billing_frequencies || []).map((f) => (<option key={f} value={f}>{f}</option>))}
                </select>
              </Field>
              <Field label="Billing mode">
                <select data-testid="project-form-mode" value={form.billing_mode} onChange={(e) => set("billing_mode", e.target.value)} className={inputCls}>
                  {(meta?.billing_modes || []).map((m) => (<option key={m} value={m}>{m}</option>))}
                </select>
              </Field>
              <Field label="Next billing date">
                <input data-testid="project-form-nextbill" type="date" value={form.next_billing_date || ""} onChange={(e) => set("next_billing_date", e.target.value)} className={inputCls} />
              </Field>
            </>
          )}

          <div className="md:col-span-2">
            <Field label="Notes">
              <textarea rows={3} value={form.notes} onChange={(e) => set("notes", e.target.value)} className={inputCls} />
            </Field>
          </div>
        </div>

        <div className="mt-8 flex items-center justify-end gap-3">
          <button onClick={onClose} className="rounded-md px-4 py-2 text-sm font-medium text-zinc-700 hover:bg-zinc-100">Cancel</button>
          <button data-testid="project-modal-save" onClick={save} disabled={saving} className="rounded-md bg-[#0066ff] px-4 py-2 text-sm font-medium text-white hover:bg-[#0052cc] disabled:opacity-50">
            {saving ? "Saving…" : (isEdit ? "Save" : "Create")}
          </button>
        </div>
      </div>
    </div>
  );
}

function MigrateModal({ onClose, onConfirm }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4" onClick={onClose}>
      <div data-testid="migrate-modal" className="w-full max-w-md rounded-2xl bg-white p-7 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <h2 className="text-xl font-semibold tracking-tight text-zinc-900">Import CRM Data</h2>
        <p className="mt-2 text-sm text-zinc-600">
          Convert every existing Lead and Subscription into a Project so everything lives on one page.
          Anything already imported is skipped (safe to re-run).
        </p>
        <ul className="mt-4 space-y-2 text-xs text-zinc-600">
          <li>• Sales/Verbal leads → <strong className="text-zinc-900">Sales Pipeline</strong></li>
          <li>• In-progress / Awaiting-payment leads → <strong className="text-zinc-900">Active Projects</strong> (100% confidence)</li>
          <li>• Subscriptions → <strong className="text-zinc-900">Passive Projects</strong>, tiered by monthly amount</li>
          <li>• Clients that don't yet exist get a "needs setup" stub for you to review</li>
        </ul>
        <div className="mt-6 flex justify-end gap-3">
          <button onClick={onClose} className="rounded-md px-4 py-2 text-sm font-medium text-zinc-700 hover:bg-zinc-100">Cancel</button>
          <button data-testid="migrate-modal-confirm" onClick={onConfirm} className="rounded-md bg-[#0066ff] px-4 py-2 text-sm font-medium text-white hover:bg-[#0052cc]">Run import</button>
        </div>
      </div>
    </div>
  );
}

function StubReviewModal({ stubs, clients, onClose, onResolved }) {
  const [idx, setIdx] = useState(0);
  const [name, setName] = useState("");
  const [mergeInto, setMergeInto] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const current = stubs[idx];

  useEffect(() => {
    if (!current) return;
    setName(current.name || "");
    setMergeInto("");
  }, [current?.user_id]);

  if (!current) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4" onClick={onClose}>
        <div className="w-full max-w-sm rounded-2xl bg-white p-7 text-center shadow-xl" onClick={(e) => e.stopPropagation()}>
          <CheckCircle2 size={32} className="mx-auto text-emerald-500" />
          <h2 className="mt-3 text-lg font-semibold text-zinc-900">All clients reviewed</h2>
          <p className="mt-1 text-sm text-zinc-500">You can always add more details later from the Clients tab.</p>
          <button onClick={onClose} className="mt-5 rounded-md bg-[#0066ff] px-4 py-2 text-sm font-medium text-white hover:bg-[#0052cc]">Done</button>
        </div>
      </div>
    );
  }

  const resolve = async (action) => {
    setSubmitting(true);
    try {
      const payload = action === "merge"
        ? { action: "merge", merge_into: mergeInto }
        : { action, new_name: name.trim() || current.name };
      if (action === "merge" && !mergeInto) { toast.error("Pick a client to merge into"); setSubmitting(false); return; }
      if ((action === "rename" || action === "keep") && !name.trim()) { toast.error("Name required"); setSubmitting(false); return; }
      await api.post(`/crm/stub-clients/${current.user_id}/resolve`, payload);
      toast.success(action === "merge" ? "Merged" : "Saved");
      await onResolved();
      // advance to next
      setIdx((i) => i + 1);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4" onClick={onClose}>
      <div data-testid="stub-modal" className="w-full max-w-lg rounded-2xl bg-white p-7 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between">
          <div>
            <div className="text-xs font-semibold uppercase tracking-widest text-zinc-500">Review · {idx + 1} of {stubs.length}</div>
            <h2 className="mt-1 text-xl font-semibold tracking-tight text-zinc-900">{current.name}</h2>
            <p className="mt-1 text-xs text-zinc-500">{current.project_count} project(s) attached · Auto-created from your subscriptions/leads</p>
          </div>
          <button onClick={onClose} className="rounded p-1 text-zinc-400 hover:bg-zinc-100"><X size={18} /></button>
        </div>

        <div className="mt-5 flex flex-col gap-4">
          <Field label="Confirm or rename this client">
            <input data-testid="stub-name" value={name} onChange={(e) => setName(e.target.value)} className={inputCls} />
          </Field>

          <div className="rounded-lg border border-zinc-200 bg-zinc-50/50 p-3">
            <div className="text-[11px] font-semibold uppercase tracking-widest text-zinc-500">Or map onto an existing client</div>
            <select data-testid="stub-merge-select" value={mergeInto} onChange={(e) => setMergeInto(e.target.value)} className={`${inputCls} mt-1.5`}>
              <option value="">Choose a client…</option>
              {clients.map((c) => (<option key={c.user_id} value={c.user_id}>{c.name}</option>))}
            </select>
          </div>
        </div>

        <div className="mt-6 flex flex-wrap items-center justify-between gap-2">
          <button
            onClick={() => setIdx((i) => Math.min(stubs.length, i + 1))}
            className="rounded-md px-3 py-2 text-xs font-medium text-zinc-500 hover:bg-zinc-100"
          >
            Skip for now
          </button>
          <div className="flex flex-wrap items-center gap-2">
            {mergeInto ? (
              <button
                data-testid="stub-merge-btn"
                onClick={() => resolve("merge")}
                disabled={submitting}
                className="rounded-md bg-emerald-500 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-600 disabled:opacity-50"
              >
                Merge into existing
              </button>
            ) : (
              <button
                data-testid="stub-keep-btn"
                onClick={() => resolve(name.trim() !== current.name.trim() ? "rename" : "keep")}
                disabled={submitting}
                className="rounded-md bg-[#0066ff] px-4 py-2 text-sm font-medium text-white hover:bg-[#0052cc] disabled:opacity-50"
              >
                Save & next
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

const inputCls = "w-full rounded-md border border-zinc-200 bg-white px-3 py-2 text-sm placeholder:text-zinc-400 focus:border-[#0066ff] focus:outline-none focus:ring-2 focus:ring-[#0066ff]/20";

function Field({ label, required, children }) {
  return (
    <label className="flex flex-col gap-1.5 text-sm">
      <span className="text-xs font-medium text-zinc-600">{label}{required && <span className="text-red-500"> *</span>}</span>
      {children}
    </label>
  );
}
